import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, BrainCircuit, Check, CheckCircle, ChevronDown, ChevronRight, ChevronUp, Copy, ExternalLink, Eye, FileText,
  ImagePlus, Link2, Loader2, Mail, MapPin, Newspaper, Palette, Plus, Route, Rss, Save, Search, Send, Trash2, X, Youtube
} from 'lucide-react';
import type { Article, ArticleExternalLink, ArticleImage, ArticleLink, ArticleWheelEntry, ArticleYouTubeVideo, GpxRoute } from '../../../types';
import {
  subscribeToArticles, createArticle, updateArticle as updateArticleFn,
  publishArticle as publishArticleFn, deleteArticle as deleteArticleFn,
  uploadArticleImage, removeArticleImage, triggerArticleGeneration,
  generateArticleImageManual, searchContentForArticle, sendArticleCampaign, fetchExternalLinkMetadata,
  captureAppScreenshots, subscribeToArticleWheel, reviewArticle
} from '../../../services/articleService';
import { fetchAllRoutes } from '../../../services/firebaseService';
import { getDateLocale } from '../../../services/localeHelpers';
import AdminArticleWheelPanel from './AdminArticleWheelPanel';
import ArticleYearStrip from './ArticleYearStrip';
import ArticleInboxRow from './ArticleInboxRow';
import ArticleFlowBoard from './ArticleFlowBoard';
import ViewSparkline from './ViewSparkline';
import { Button } from '../../ui/Button';
import { Tabs } from '../../ui/Tabs';
import { todayYmd } from '../../../utils/articleWheelDates';

const RSS_FEED_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/articlesFeed';

/** Fargekode per status. `pending_review` skal skille seg ut — den venter på deg. */
const STATUS_BADGE: Record<string, string> = {
  published: 'bg-emerald-500/10 text-emerald-400',
  pending_review: 'bg-emerald-500/20 text-emerald-300',
  approved: 'bg-emerald-500/10 text-emerald-400',
  draft: 'bg-amber-500/10 text-amber-400',
};

const AdminArticlesPanel: React.FC = () => {
  const { t } = useTranslation();

  // List state
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  // Modulvisning — tre linser på samme data. Innboks er default: det du gjør
  // oftest er å avgjøre, ikke å planlegge.
  const [moduleTab, setModuleTab] = useState<'flyt' | 'innboks' | 'arshjul' | 'alle'>('flyt');
  const [wheelEntries, setWheelEntries] = useState<ArticleWheelEntry[]>([]);
  const [wheelHorizon, setWheelHorizon] = useState<string | undefined>(undefined);
  const [selectedWheelId, setSelectedWheelId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('alle');

  useEffect(() => subscribeToArticleWheel((w) => {
    setWheelEntries(w?.entries ?? []);
    setWheelHorizon(w?.planHorizonYmd);
  }), []);

  // Køen: alt som venter på en avgjørelse, nyeste først. `generating` er med
  // fordi saken skal ligge der den kommer til å ligge, ikke dukke opp brått.
  /**
   * Køen er PRIORITERT, ikke bare filtrert. Rekkefølgen er det som gjør den til
   * en avgjørelseskø og ikke enda en liste:
   *  1. crawler-treff sorteres på relevansscore (høyest først)
   *  2. årshjul-saker på nærhet til anledningen — en merkedag i morgen haster
   *     mer enn en om tre uker
   *  3. `generating` nederst; den kan ikke avgjøres ennå
   */
  const inboxArticles = useMemo(() => {
    const today = todayYmd();
    const rank = (a: Article): number => {
      if (a.status === 'generating') return -1e9;
      const rel = (a as { relevance_score?: number }).relevance_score;
      if (typeof rel === 'number') return 1e6 + rel;
      if (a.occasion_ymd) {
        const days = Math.round(
          (Date.parse(`${a.occasion_ymd}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000,
        );
        return 1e5 - Math.max(days, -365); // nærmere anledning = høyere
      }
      // Utkast havner nederst, men de HAVNER der. Uten dette blir de liggende
      // usett for alltid — som de fem AI-artiklene fra den gamle 1./15.-jobben.
      return a.status === 'draft' ? -1000 : 0;
    };

    const due = (a: Article) =>
      a.status === 'draft' && (!a.snoozed_until || a.snoozed_until <= today);

    return articles
      .filter((a) => a.status === 'pending_review' || a.status === 'generating' || due(a))
      .sort((a, b) => rank(b) - rank(a));
  }, [articles]);

  /**
   * Merkelappen på fanen betyr HASTER, ikke «alt som ligger her». Utkast er
   * ubesluttet arbeid, men de venter ikke på en beslutning i dag — teller vi dem
   * med, sier fanen 7 mens tellerstripa og Flyt sier 0, og begge har rett.
   */
  const urgentCount = useMemo(
    () => articles.filter((a) => a.status === 'pending_review' || a.status === 'generating').length,
    [articles],
  );

  const inboxDrafts = useMemo(
    () => inboxArticles.filter((a) => a.status === 'draft'),
    [inboxArticles],
  );
  const inboxUrgent = useMemo(
    () => inboxArticles.filter((a) => a.status !== 'draft'),
    [inboxArticles],
  );

  /** Triagering på Alle-fanen. Statusene er de faktiske stegene i løypa. */
  const STATUS_FILTERS = useMemo(() => ([
    { key: 'alle', label: 'Alle', match: () => true },
    { key: 'generating', label: 'Genereres', match: (a: Article) => a.status === 'generating' },
    { key: 'pending_review', label: 'Til godkjenning', match: (a: Article) => a.status === 'pending_review' },
    { key: 'approved', label: 'Godkjent', match: (a: Article) => a.status === 'approved' },
    { key: 'draft', label: 'Utkast', match: (a: Article) => a.status === 'draft' },
    { key: 'published', label: 'Publisert', match: (a: Article) => a.status === 'published' },
  ]), []);

  const visibleArticles = useMemo(() => {
    const f = STATUS_FILTERS.find((x) => x.key === statusFilter) ?? STATUS_FILTERS[0];
    return articles.filter(f.match);
  }, [articles, statusFilter, STATUS_FILTERS]);

  /**
   * Tellerstripe. Kandidater utelates til crawleren finnes — en hardkodet 0
   * ville lest som «crawleren fant ingenting», ikke «crawleren finnes ikke».
   */
  const pipelineCounts = useMemo(() => {
    const now = new Date();
    const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const publishedThisMonth = articles.filter((a) => {
      if (a.status !== 'published') return false;
      const d = a.published_at?.toDate?.() ?? (a.published_at instanceof Date ? a.published_at : null);
      if (!d) return false;
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` === thisMonth;
    }).length;

    return [
      { key: 'genererer', label: 'Genererer', n: articles.filter((a) => a.status === 'generating').length },
      { key: 'venter', label: 'Til godkjenning', n: articles.filter((a) => a.status === 'pending_review').length, active: true },
      { key: 'planlagt', label: 'Planlagt', n: articles.filter((a) => a.status === 'approved').length },
      { key: 'publisert', label: 'Publisert i mnd', n: publishedThisMonth },
    ];
  }, [articles]);

  const handleInboxPublish = useCallback(async (a: Article) => {
    // Ett trykk her kan sende masse-e-post. Krev bekreftelse når den er på —
    // samme regel som i hurtiggodkjennings-modalen.
    if (a.distribute_via_email && !window.confirm(
      `«${a.title}» er merket for e-post. Publisering sender den til alle med varsler på. Fortsette?`,
    )) return;
    try {
      await reviewArticle(a.id, 'publish_now');
      setActionMsg(`Publisert: ${a.title}`);
    } catch (e) {
      setActionMsg(`Feil: ${e instanceof Error ? e.message : 'kunne ikke publisere'}`);
    }
  }, []);

  const handleInboxDelete = useCallback(async (a: Article) => {
    try {
      await reviewArticle(a.id, 'reject');
      setActionMsg(`Slettet: ${a.title || 'uten tittel'}`);
    } catch (e) {
      setActionMsg(`Feil: ${e instanceof Error ? e.message : 'kunne ikke slette'}`);
    }
  }, []);

  const handleInboxSchedule = useCallback(async (a: Article) => {
    const ymd = a.occasion_ymd || a.scheduled_publish_ymd;
    if (!ymd) {
      setActionMsg('Mangler dato — sett anledningsdato på merkedagen først.');
      return;
    }
    try {
      await reviewArticle(a.id, 'approve_scheduled', ymd);
      setActionMsg(`Godkjent for ${ymd}.`);
    } catch (e) {
      setActionMsg(`Feil: ${e instanceof Error ? e.message : 'kunne ikke planlegge'}`);
    }
  }, []);

  const handleInboxLater = useCallback(async (a: Article) => {
    try {
      const res = await reviewArticle(a.id, 'later');
      const until = (res as { snoozedUntil?: string }).snoozedUntil;
      setActionMsg(until ? `Utsatt — kommer tilbake i køen ${until}.` : 'Lagt tilbake som utkast.');
    } catch (e) {
      setActionMsg(`Feil: ${e instanceof Error ? e.message : 'kunne ikke oppdatere'}`);
    }
  }, []);

  // Editor state
  const [editing, setEditing] = useState<Article | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [summary, setSummary] = useState('');
  const [slug, setSlug] = useState('');
  const [images, setImages] = useState<ArticleImage[]>([]);
  const [distributeEmail, setDistributeEmail] = useState(false);
  const [articleType, setArticleType] = useState<'blog' | 'release_notes'>('blog');
  const [articleVersion, setArticleVersion] = useState('');
  const [articleImpact, setArticleImpact] = useState<'major' | 'minor'>('minor');
  const [showPreview, setShowPreview] = useState(false);
  const [externalLinks, setExternalLinks] = useState<ArticleExternalLink[]>([]);
  const [externalUrlInput, setExternalUrlInput] = useState('');
  const [externalCommentInput, setExternalCommentInput] = useState('');
  const [externalTitleLoading, setExternalTitleLoading] = useState(false);

  // Action states
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generatingImage, setGeneratingImage] = useState<string | null>(null); // 'morsom' | 'nostalgisk' | null
  const [capturingShots, setCapturingShots] = useState(false);
  const [previewArticle, setPreviewArticle] = useState<Article | null>(null);
  const [showRssInfo, setShowRssInfo] = useState(false);
  const [rssCopied, setRssCopied] = useState(false);
  const [customTopic, setCustomTopic] = useState('');
  const [articleAngle, setArticleAngle] = useState('');
  const [showCustomTopic, setShowCustomTopic] = useState(false);

  // YouTube videos state
  const [youtubeVideos, setYoutubeVideos] = useState<ArticleYouTubeVideo[]>([]);
  const [ytUrlInput, setYtUrlInput] = useState('');
  const [ytAdding, setYtAdding] = useState(false);

  // Campaign email state
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [confirmCampaign, setConfirmCampaign] = useState(false);

  // Linked content state
  const [linkedContent, setLinkedContent] = useState<ArticleLink[]>([]);
  // Routes: region-grouped toggle list
  const [allRoutes, setAllRoutes] = useState<GpxRoute[]>([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());
  // Spots: browseable container with search
  const [browseSpots, setBrowseSpots] = useState<ArticleLink[]>([]);
  const [spotsLoading, setSpotsLoading] = useState(false);
  const [spotSearchQuery, setSpotSearchQuery] = useState('');

  // Subscribe to articles
  useEffect(() => {
    const unsub = subscribeToArticles((data) => {
      setArticles(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const openEditor = useCallback(async (article: Article) => {
    setEditing(article);
    setTitle(article.title);
    setContent(article.content);
    setSummary(article.summary);
    setSlug(article.slug);
    setImages([...(article.images || [])]);
    setDistributeEmail(article.distribute_via_email);
    setArticleType(article.type === 'release_notes' ? 'release_notes' : 'blog');
    setArticleVersion(article.version || '');
    setArticleImpact(article.impact === 'major' ? 'major' : 'minor');
    setLinkedContent([...(article.linked_content || [])]);
    setExternalLinks([...(article.external_links || [])]);
    setYoutubeVideos(article.youtube_videos?.length ? [...article.youtube_videos] : article.youtube_video ? [article.youtube_video] : []);
    setYtUrlInput('');
    setExternalUrlInput('');
    setExternalCommentInput('');
    setExternalTitleLoading(false);
    setYtAdding(false);
    setConfirmCampaign(false);
    setSpotSearchQuery('');
    setBrowseSpots([]);
    setExpandedRegions(new Set());
    setShowPreview(false);
    setConfirmDelete(false);
    setActionMsg(null);
    // Fetch public routes for linking
    setRoutesLoading(true);
    try {
      const routes = await fetchAllRoutes();
      setAllRoutes(routes.filter(r => r.status === 'public'));
    } catch (e) { console.warn('Failed to fetch routes:', e); }
    setRoutesLoading(false);
    // Fetch initial spots (browse mode)
    setSpotsLoading(true);
    try {
      const spots = await searchContentForArticle('', 'spot');
      setBrowseSpots(spots);
    } catch (e) { console.warn('Failed to browse spots:', e); }
    setSpotsLoading(false);
  }, []);

  const closeEditor = useCallback(() => {
    setEditing(null);
    setActionMsg(null);
    setConfirmDelete(false);
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await createArticle(t('admin.articles.new_article'));
      setActionMsg(null);
    } catch (e: any) {
      setActionMsg(`Feil: ${e.message}`);
    }
    setCreating(false);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setActionMsg(null);
    try {
      const topic = customTopic.trim() || undefined;
      const angle = articleAngle.trim() || undefined;
      await triggerArticleGeneration(topic, angle);
      setActionMsg(t('admin.articles_generation_started', 'AI-generering startet! Artikkelen dukker opp i listen når den er klar.'));
      setCustomTopic('');
      setArticleAngle('');
      setShowCustomTopic(false);
      setTimeout(() => setActionMsg(null), 6000);
    } catch (e: any) {
      setActionMsg(`Feil: ${e.message}`);
    }
    setGenerating(false);
  };

  const handleCaptureScreenshots = async () => {
    if (!editing) return;
    setCapturingShots(true);
    setActionMsg(t('admin.articles.capturing_shots', 'Tar skjermbilder av appen … (kan ta 20–40 sek)'));
    try {
      const res = await captureAppScreenshots(editing.id);
      if (res.images) setImages(res.images);
      setActionMsg(t('admin.articles.captured_shots', `La til ${res.added} skjermbilder`));
      setTimeout(() => setActionMsg(null), 4000);
    } catch (e: any) {
      setActionMsg(`Feil: ${e.message}`);
    }
    setCapturingShots(false);
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await updateArticleFn({
        articleId: editing.id,
        title,
        content,
        summary,
        slug,
        images,
        distribute_via_email: distributeEmail,
        linked_content: linkedContent,
        external_links: externalLinks,
        youtube_videos: youtubeVideos,
        type: articleType,
        version: articleType === 'release_notes' ? articleVersion.trim() : undefined,
        impact: articleType === 'release_notes' ? articleImpact : undefined,
      });
      setActionMsg(t('admin.articles_saved', 'Lagret'));
      setTimeout(() => setActionMsg(null), 3000);
    } catch (e: any) {
      setActionMsg(`Feil: ${e.message}`);
    }
    setSaving(false);
  };

  const handlePublish = async () => {
    if (!editing) return;
    setPublishing(true);
    try {
      // Save first
      await updateArticleFn({
        articleId: editing.id,
        title,
        content,
        summary,
        slug,
        images,
        distribute_via_email: distributeEmail,
        linked_content: linkedContent,
        external_links: externalLinks,
        youtube_videos: youtubeVideos,
        type: articleType,
        version: articleType === 'release_notes' ? articleVersion.trim() : undefined,
        impact: articleType === 'release_notes' ? articleImpact : undefined,
      });
      const result = await publishArticleFn(editing.id);
      const emailMsg = distributeEmail
        ? ` E-post sendt til ${result.emailSent} brukere.`
        : '';
      setActionMsg(`Publisert!${emailMsg}`);
      closeEditor();
    } catch (e: any) {
      setActionMsg(`Feil: ${e.message}`);
    }
    setPublishing(false);
  };

  const handleDelete = async () => {
    if (!editing) return;
    setDeleting(true);
    try {
      await deleteArticleFn(editing.id);
      setActionMsg(null);
      closeEditor();
    } catch (e: any) {
      setActionMsg(`Feil: ${e.message}`);
    }
    setDeleting(false);
  };

  // YouTube video helpers
  const parseYouTubeId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?.*v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/|youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const p of patterns) {
      const m = url.trim().match(p);
      if (m) return m[1];
    }
    return null;
  };

  const handleAddYouTubeVideo = async () => {
    const videoId = parseYouTubeId(ytUrlInput);
    if (!videoId) {
      setActionMsg('Ugyldig YouTube-URL');
      setTimeout(() => setActionMsg(null), 3000);
      return;
    }
    if (youtubeVideos.some(v => v.videoId === videoId)) {
      setActionMsg(t('admin.articles.video_already_added'));
      setTimeout(() => setActionMsg(null), 3000);
      return;
    }
    setYtAdding(true);
    try {
      let title = 'Ukjent tittel';
      let channelTitle = t('admin.articles.unknown_channel');
      let thumbnailUrl: string | undefined = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      try {
        const oembedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
        const res = await fetch(oembedUrl);
        if (res.ok) {
          const data = await res.json();
          title = data.title || title;
          channelTitle = data.author_name || channelTitle;
          thumbnailUrl = data.thumbnail_url || thumbnailUrl;
        }
      } catch { /* oEmbed feilet — bruker fallback-verdier */ }
      const newVideo: ArticleYouTubeVideo = {
        videoId, title, channelTitle,
        url: `https://www.youtube.com/watch?v=${videoId}`,
        thumbnailUrl,
      };
      setYoutubeVideos(prev => [...prev, newVideo]);
      setYtUrlInput('');
    } catch (e: any) {
      setActionMsg(`Kunne ikke legge til video: ${e.message}`);
      setTimeout(() => setActionMsg(null), 3000);
    }
    setYtAdding(false);
  };

  const handleRemoveYouTubeVideo = (videoId: string) => {
    setYoutubeVideos(prev => prev.filter(v => v.videoId !== videoId));
  };

  // Campaign email handler
  const handleSendCampaign = async () => {
    if (!editing) return;
    setSendingCampaign(true);
    try {
      // Save first to ensure latest content
      await updateArticleFn({
        articleId: editing.id,
        title, content, summary, slug, images,
        linked_content: linkedContent,
        external_links: externalLinks,
        youtube_videos: youtubeVideos,
      });
      const result = await sendArticleCampaign(editing.id);
      setActionMsg(`Kampanje-epost sendt til ${result.emailSent} brukere!`);
      setConfirmCampaign(false);
      setTimeout(() => setActionMsg(null), 5000);
    } catch (e: any) {
      setActionMsg(`Feil: ${e.message}`);
    }
    setSendingCampaign(false);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editing || !e.target.files?.length) return;
    setUploadingImage(true);
    try {
      const file = e.target.files[0];
      const url = await uploadArticleImage(editing.id, file);
      const newImage: ArticleImage = {
        url,
        caption: '',
        order: images.length,
      };
      const updated = [...images, newImage];
      setImages(updated);
      // Persist immediately
      await updateArticleFn({ articleId: editing.id, images: updated });
    } catch (err: any) {
      setActionMsg(`Bildeopplasting feilet: ${err.message}`);
    }
    setUploadingImage(false);
    // Reset input
    e.target.value = '';
  };

  const handleRemoveImage = async (idx: number) => {
    if (!editing) return;
    const img = images[idx];
    try {
      await removeArticleImage(img.url);
    } catch { /* ignore storage errors */ }
    const updated = images.filter((_, i) => i !== idx).map((img, i) => ({ ...img, order: i }));
    setImages(updated);
    await updateArticleFn({ articleId: editing.id, images: updated });
  };

  const moveImage = async (idx: number, dir: -1 | 1) => {
    if (!editing) return;
    const target = idx + dir;
    if (target < 0 || target >= images.length) return;
    const copy = [...images];
    [copy[idx], copy[target]] = [copy[target], copy[idx]];
    const reordered = copy.map((img, i) => ({ ...img, order: i }));
    setImages(reordered);
    await updateArticleFn({ articleId: editing.id, images: reordered });
  };

  const handleGenerateImage = async (style: 'morsom' | 'nostalgisk') => {
    if (!editing) return;
    setGeneratingImage(style);
    setActionMsg(null);
    try {
      const result = await generateArticleImageManual(editing.id, style);
      // Additivt: behold eksisterende bilder (skjermbilder etc.) og legg det nye til.
      if (result.images) {
        setImages(result.images);
      } else if (result.image) {
        setImages(prev => [...prev, result.image]);
      }
      setActionMsg(t('admin.articles_image_generated', `Nytt {{style}}-bilde generert!`).replace('{{style}}', style));
      setTimeout(() => setActionMsg(null), 4000);
    } catch (e: any) {
      setActionMsg(`Feil: ${e.message}`);
    }
    setGeneratingImage(null);
  };

  // Routes grouped by region
  const regionLabelMap: Record<string, string> = {
    'north': 'Nord-Norge', 'central': 'Midt-Norge', 'south': 'Sør-Norge',
    'Nord-Norge': 'Nord-Norge', 'Midt-Norge': 'Midt-Norge', 'Sør-Norge': 'Sør-Norge',
    'Sverige': 'Sverige', 'Götaland': 'Sverige', 'Svealand': 'Sverige', 'Norrland': 'Sverige',
    'sweden': 'Sverige', 'SE': 'Sverige',
  };
  const regionOrder = ['Nord-Norge', 'Midt-Norge', 'Sør-Norge', 'Sverige', 'Usortert'];
  const routesByRegion = useMemo(() => {
    const groups: Record<string, GpxRoute[]> = { 'Nord-Norge': [], 'Midt-Norge': [], 'Sør-Norge': [], 'Sverige': [], 'Usortert': [] };
    allRoutes.forEach(r => {
      const label = r.region ? (regionLabelMap[r.region] || 'Usortert') : 'Usortert';
      (groups[label] || groups['Usortert']).push(r);
    });
    return groups;
  }, [allRoutes]);

  const toggleRegion = (region: string) => {
    setExpandedRegions(prev => {
      const next = new Set(prev);
      if (next.has(region)) next.delete(region); else next.add(region);
      return next;
    });
  };

  const isRouteLinked = (routeId: string) => linkedContent.some(lc => lc.id === routeId && lc.type === 'route');

  const toggleRouteLink = (route: GpxRoute) => {
    if (isRouteLinked(route.id)) {
      setLinkedContent(linkedContent.filter(lc => !(lc.id === route.id && lc.type === 'route')));
    } else {
      setLinkedContent([...linkedContent, {
        id: route.id,
        name: route.name,
        type: 'route',
        slug: route.slug || '',
        imageUrl: route.imageUrls?.[0] || '',
      }]);
    }
  };

  // Spots search with debounce
  const spotSearchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const handleSpotSearch = (query: string) => {
    setSpotSearchQuery(query);
    if (spotSearchTimeoutRef.current) clearTimeout(spotSearchTimeoutRef.current);
    spotSearchTimeoutRef.current = setTimeout(async () => {
      setSpotsLoading(true);
      try {
        const results = await searchContentForArticle(query.trim(), 'spot');
        setBrowseSpots(results);
      } catch (e) { console.warn('Spot search failed:', e); setBrowseSpots([]); }
      setSpotsLoading(false);
    }, 300);
  };

  const addLinkedContent = (item: ArticleLink) => {
    if (linkedContent.some(lc => lc.id === item.id && lc.type === item.type)) return;
    setLinkedContent([...linkedContent, item]);
  };

  const removeLinkedContent = (idx: number) => {
    setLinkedContent(linkedContent.filter((_, i) => i !== idx));
  };

  const normalizeExternalUrl = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
      const parsed = new URL(withProtocol);
      if (!['http:', 'https:'].includes(parsed.protocol)) return null;
      return parsed.toString();
    } catch {
      return null;
    }
  };

  const removeExternalLink = (idx: number) => {
    setExternalLinks(externalLinks.filter((_, i) => i !== idx));
  };

  const handleAddExternalLink = async () => {
    const normalizedUrl = normalizeExternalUrl(externalUrlInput);
    if (!normalizedUrl) {
      setActionMsg(t('admin.articles_external_url_invalid', 'Ugyldig lenke. Bruk en full URL.'));
      setTimeout(() => setActionMsg(null), 3000);
      return;
    }

    if (externalLinks.some((l) => l.url === normalizedUrl)) {
      setActionMsg(t('admin.articles_external_exists', 'Lenken er allerede lagt til.'));
      setTimeout(() => setActionMsg(null), 3000);
      return;
    }

    setExternalTitleLoading(true);
    try {
      const meta = await fetchExternalLinkMetadata(normalizedUrl);
      let fallbackTitle = normalizedUrl;
      try {
        fallbackTitle = new URL(normalizedUrl).hostname;
      } catch {
        // no-op
      }
      const resolvedTitle = (meta?.data?.title || fallbackTitle).trim() || fallbackTitle;

      const nextLink: ArticleExternalLink = {
        url: normalizedUrl,
        title: resolvedTitle,
      };
      const comment = externalCommentInput.trim();
      if (comment) nextLink.comment = comment;

      setExternalLinks((prev) => [...prev, nextLink]);
      setExternalUrlInput('');
      setExternalCommentInput('');
    } catch (e: any) {
      setActionMsg(`Feil: ${e?.message || 'Kunne ikke hente tittel fra lenken'}`);
      setTimeout(() => setActionMsg(null), 3500);
    } finally {
      setExternalTitleLoading(false);
    }
  };

  const getLinkUrl = (item: ArticleLink) => {
    if (item.type === 'route') {
      return item.slug
        ? `https://app.fricamp.com/rute/${item.slug}-${item.id}`
        : `https://app.fricamp.com/share/route/${item.id}`;
    }
    return `https://app.fricamp.com/share/spot/${item.id}`;
  };

  const updateCaption = (idx: number, caption: string) => {
    const copy = [...images];
    copy[idx] = { ...copy[idx], caption };
    setImages(copy);
  };

  const formatDate = (ts: any) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // ─── Editor View ───
  if (editing) {
    return (
      <div className="space-y-4 animate-in slide-in-from-bottom-4 max-h-[85vh] overflow-y-auto pr-2 custom-scrollbar">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={closeEditor} className="p-2 rounded-card bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-secondary)] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60">
            <ArrowLeft size={16} />
          </button>
          <div className="flex-1">
            <h3 className="text-tiny font-black text-emerald-400 uppercase tracking-widest">
              {editing.status === 'published' ? t('admin.articles_status_published', 'Publisert') : t('admin.articles_edit', 'Rediger artikkel')}
            </h3>
            {editing.generated_by === 'ai' && editing.ai_prompt_context && (
              <p className="text-xs text-[var(--text-muted)] mt-0.5">AI-generert for {editing.ai_prompt_context}</p>
            )}
          </div>
          {actionMsg && (
            <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${actionMsg.startsWith('Feil') || actionMsg.startsWith('Ugyldig') || actionMsg.startsWith('Kunne ikke') || actionMsg.startsWith('Videoen er allerede') ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
              {actionMsg}
            </span>
          )}
        </div>

        {/* Title */}
        <div className="space-y-1.5">
          <label className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest px-1">{t('admin.articles_field_title', 'Tittel')}</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Artikkeltittel..."
            className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card px-4 py-3 text-[12px] text-[var(--text-primary)] focus:outline-none focus:border-emerald-500 placeholder:text-[var(--text-muted)]"
          />
        </div>

        {/* Slug */}
        <div className="space-y-1.5">
          <label className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest px-1">Slug (URL)</label>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="url-vennlig-tittel"
            className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card px-4 py-3 text-tiny text-[var(--text-secondary)] font-mono focus:outline-none focus:border-emerald-500 placeholder:text-[var(--text-muted)]"
          />
        </div>

        {/* Linked content (routes & spots) */}
        <div className="space-y-3">
          <label className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest px-1 flex items-center gap-2">
            <Link2 size={12} />
            {t('admin.articles_linked_content', 'Lenket innhold')}
            {linkedContent.length > 0 && (
              <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                {linkedContent.length} valgt
              </span>
            )}
          </label>

          {/* Selected items summary */}
          {linkedContent.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {linkedContent.map((item, idx) => (
                <div key={`${item.type}-${item.id}`} className="flex items-center gap-1.5 bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-card px-2.5 py-1.5">
                  {item.type === 'route' ? <Route size={10} className="text-emerald-400 flex-shrink-0" /> : <MapPin size={10} className="text-emerald-400 flex-shrink-0" />}
                  <span className="text-xs text-[var(--text-secondary)] truncate max-w-[140px]">{item.name}</span>
                  <button onClick={() => removeLinkedContent(idx)} className="text-[var(--text-muted)] hover:text-red-400 flex-shrink-0"><X size={10} /></button>
                </div>
              ))}
            </div>
          )}

          {/* Routes: Region-grouped toggle list */}
          <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-card p-3 space-y-1">
            <div className="flex items-center gap-2 mb-2">
              <Route size={12} className="text-emerald-400" />
              <span className="text-3xs font-black text-emerald-400 uppercase tracking-widest">{t('admin.articles_link_route', 'Ruter')}</span>
              {routesLoading && <Loader2 size={10} className="text-[var(--text-muted)] animate-spin" />}
            </div>
            {!routesLoading && allRoutes.length === 0 && (
              <p className="text-xs text-[var(--text-muted)] py-1">Ingen offentlige ruter funnet</p>
            )}
            {regionOrder.map(region => {
              const routes = routesByRegion[region] || [];
              if (routes.length === 0) return null;
              const selectedCount = routes.filter(r => isRouteLinked(r.id)).length;
              const isExpanded = expandedRegions.has(region);
              return (
                <div key={region} className="border border-[var(--border-primary)] rounded-card overflow-hidden">
                  <button
                    onClick={() => toggleRegion(region)}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface-alt)] transition-colors text-left"
                  >
                    {isExpanded ? <ChevronDown size={12} className="text-[var(--text-muted)]" /> : <ChevronRight size={12} className="text-[var(--text-muted)]" />}
                    <span className="text-xs font-bold text-[var(--text-secondary)] flex-1">{region}</span>
                    <span className="text-tiny text-[var(--text-muted)]">{routes.length} ruter</span>
                    {selectedCount > 0 && (
                      <span className="text-tiny font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full">{selectedCount} valgt</span>
                    )}
                  </button>
                  {isExpanded && (
                    <div className="px-2 py-1 space-y-0.5 max-h-48 overflow-y-auto">
                      {routes.map(route => (
                        <button
                          key={route.id}
                          onClick={() => toggleRouteLink(route)}
                          className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-card transition-all text-left ${
                            isRouteLinked(route.id) ? 'bg-emerald-500/10 border border-emerald-500/20' : 'hover:bg-[var(--bg-surface-alt)] border border-transparent'
                          }`}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                            isRouteLinked(route.id) ? 'bg-emerald-500 border-emerald-500' : 'border-[var(--border-secondary)]'
                          }`}>
                            {isRouteLinked(route.id) && <Check size={10} className="text-white" />}
                          </div>
                          <span className={`text-xs truncate flex-1 ${isRouteLinked(route.id) ? 'text-emerald-300 font-semibold' : 'text-[var(--text-secondary)]'}`}>
                            {route.name}
                          </span>
                          {route.distance && (
                            <span className="text-tiny text-[var(--text-muted)] flex-shrink-0">{route.distance.toFixed(1)} km</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Spots: Browseable container with search */}
          <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-card p-3 space-y-2">
            <div className="flex items-center gap-2">
              <MapPin size={12} className="text-emerald-400" />
              <span className="text-3xs font-black text-emerald-400 uppercase tracking-widest">{t('admin.articles_link_spot', 'Plasser')}</span>
              {spotsLoading && <Loader2 size={10} className="text-[var(--text-muted)] animate-spin" />}
            </div>
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <input
                type="text"
                value={spotSearchQuery}
                onChange={(e) => handleSpotSearch(e.target.value)}
                placeholder={t('admin.articles_link_search_placeholder', 'Søk etter navn...')}
                className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card pl-8 pr-3 py-1.5 text-xs text-[var(--text-secondary)] focus:outline-none focus:border-emerald-500 placeholder:text-[var(--text-muted)]"
              />
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {browseSpots.filter(s => !linkedContent.some(lc => lc.id === s.id && lc.type === 'spot')).map(spot => (
                <button
                  key={spot.id}
                  onClick={() => addLinkedContent(spot)}
                  className="w-full flex items-center gap-2.5 p-2 rounded-card hover:bg-emerald-500/10 transition-all text-left group"
                >
                  {spot.imageUrl ? (
                    <img src={spot.imageUrl} alt="" className="w-8 h-8 object-cover rounded flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded bg-[var(--bg-surface-alt)] flex items-center justify-center flex-shrink-0">
                      <MapPin size={12} className="text-emerald-500/40" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-[var(--text-secondary)] group-hover:text-emerald-300 truncate block">{spot.name}</span>
                    {spot.county && <span className="text-tiny text-[var(--text-muted)]">{spot.county}</span>}
                  </div>
                  <Plus size={12} className="text-[var(--text-muted)] group-hover:text-emerald-400 ml-auto flex-shrink-0" />
                </button>
              ))}
              {!spotsLoading && browseSpots.filter(s => !linkedContent.some(lc => lc.id === s.id && lc.type === 'spot')).length === 0 && (
                <p className="text-xs text-[var(--text-muted)] text-center py-2">
                  {t('admin.articles_link_no_results', 'Ingen treff')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* External article links */}
        <div className="space-y-3">
          <label className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest px-1 flex items-center gap-2">
            <ExternalLink size={12} />
            {t('admin.articles_external_links', 'Eksterne lenker')}
            {externalLinks.length > 0 && (
              <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                {externalLinks.length} lagt til
              </span>
            )}
          </label>

          <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-card p-3 space-y-2">
            <input
              type="text"
              value={externalUrlInput}
              onChange={(e) => setExternalUrlInput(e.target.value)}
              placeholder={t('admin.articles_external_url_placeholder', 'https://...')}
              className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card px-3 py-2 text-xs text-[var(--text-secondary)] focus:outline-none focus:border-emerald-500 placeholder:text-[var(--text-muted)]"
            />
            <input
              type="text"
              value={externalCommentInput}
              onChange={(e) => setExternalCommentInput(e.target.value)}
              placeholder={t('admin.articles_external_comment_placeholder', 'Kommentar (valgfritt)')}
              className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card px-3 py-2 text-xs text-[var(--text-secondary)] focus:outline-none focus:border-emerald-500 placeholder:text-[var(--text-muted)]"
            />
            <button
              onClick={handleAddExternalLink}
              disabled={externalTitleLoading || !externalUrlInput.trim()}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-card bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 hover:bg-emerald-500/15 transition-all text-2xs font-black uppercase tracking-widest disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
            >
              {externalTitleLoading ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              {externalTitleLoading
                ? t('admin.articles_external_fetching', 'Henter sidetittel...')
                : t('admin.articles_external_add', 'Legg til lenke')}
            </button>

            {externalLinks.length > 0 && (
              <div className="space-y-1.5 pt-1">
                {externalLinks.map((link, idx) => (
                  <div key={`${link.url}-${idx}`} className="flex items-start gap-2 p-2 rounded-card bg-[var(--bg-surface-alt)] border border-[var(--border-primary)]">
                    <ExternalLink size={12} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-[var(--text-primary)] truncate">{link.title}</p>
                      <p className="text-xs text-[var(--text-muted)] truncate">{link.url}</p>
                      {link.comment && (
                        <p className="text-xs text-[var(--text-secondary)] mt-1">{link.comment}</p>
                      )}
                    </div>
                    <button
                      onClick={() => removeExternalLink(idx)}
                      className="text-[var(--text-muted)] hover:text-red-400 flex-shrink-0"
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="space-y-1.5">
          <label className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest px-1">{t('admin.articles_field_summary', 'Sammendrag (for e-post og RSS)')}</label>
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder={t('admin.articles_summary_placeholder', 'Kort utdrag for e-post og RSS-feed...')}
            rows={3}
            className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card p-4 text-tiny text-[var(--text-secondary)] focus:outline-none focus:border-emerald-500 resize-none leading-relaxed placeholder:text-[var(--text-muted)]"
          />
        </div>

        {/* Content (HTML editor) */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between px-1">
            <label className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('admin.articles_field_content', 'Innhold (HTML)')}</label>
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-1.5 text-xs font-bold text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              <Eye size={12} />
              {showPreview ? t('admin.articles.hide_preview') : t('admin.articles.show_preview')}
            </button>
          </div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('admin.articles_content_placeholder', 'Skriv artikkelinnhold her (HTML)...')}
            className="w-full h-[35vh] bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card p-4 text-tiny text-[var(--text-secondary)] font-mono focus:outline-none focus:border-emerald-500 resize-none leading-relaxed placeholder:text-[var(--text-muted)]"
          />
        </div>

        {/* HTML Preview */}
        {showPreview && content && (
          <div className="space-y-1.5">
            <label className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest px-1">Forhåndsvisning</label>
            <div
              className="bg-white rounded-card p-6 text-[12px] text-[var(--text-primary)] leading-relaxed prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </div>
        )}

        {/* Images */}
        <div className="space-y-2">
          <label className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest px-1">{t('admin.articles_field_images', 'Bilder')}</label>

          {images.length > 0 && (
            <div className="space-y-2">
              {images.sort((a, b) => a.order - b.order).map((img, idx) => (
                <div key={img.url} className="flex items-center gap-3 bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-card p-2">
                  <img src={img.url} alt="" className="w-16 h-16 object-cover rounded-card flex-shrink-0" />
                  <input
                    type="text"
                    value={img.caption || ''}
                    onChange={(e) => updateCaption(idx, e.target.value)}
                    onBlur={() => { if (editing) updateArticleFn({ articleId: editing.id, images }); }}
                    placeholder="Bildetekst (valgfritt)..."
                    className="flex-1 bg-transparent border-none text-xs text-[var(--text-secondary)] focus:outline-none placeholder:text-[var(--text-muted)]"
                  />
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => moveImage(idx, -1)} disabled={idx === 0} className="p-1 rounded-card hover:bg-[var(--bg-surface-alt)] text-[var(--text-muted)] disabled:opacity-20">
                      <ChevronUp size={14} />
                    </button>
                    <button onClick={() => moveImage(idx, 1)} disabled={idx === images.length - 1} className="p-1 rounded-card hover:bg-[var(--bg-surface-alt)] text-[var(--text-muted)] disabled:opacity-20">
                      <ChevronDown size={14} />
                    </button>
                    <button onClick={() => handleRemoveImage(idx)} className="p-1 rounded-card hover:bg-red-500/10 text-red-400/60 hover:text-red-400">
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <label className="flex items-center justify-center gap-2 w-full py-3 rounded-card bg-[var(--bg-surface-alt)] border border-dashed border-[var(--border-secondary)] hover:border-emerald-500/30 text-[var(--text-muted)] hover:text-emerald-400 cursor-pointer transition-all text-xs font-bold">
            {uploadingImage ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
            {uploadingImage ? t('admin.articles.uploading') : t('admin.articles.upload_image')}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              disabled={uploadingImage}
            />
          </label>

          {/* AI Image Generation Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => handleGenerateImage('morsom')}
              disabled={!!generatingImage || !content.trim()}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-card bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/15 transition-all text-2xs font-black uppercase tracking-widest disabled:opacity-30"
            >
              {generatingImage === 'morsom' ? <Loader2 size={14} className="animate-spin" /> : <Palette size={14} />}
              {generatingImage === 'morsom'
                ? t('admin.articles_image_generating', 'Genererer...')
                : t('admin.articles_image_style_funny', 'Morsom stil')}
            </button>
            <button
              onClick={() => handleGenerateImage('nostalgisk')}
              disabled={!!generatingImage || !content.trim()}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-card bg-amber-500/10 border border-amber-500/20 text-amber-400 hover:bg-amber-500/15 transition-all text-2xs font-black uppercase tracking-widest disabled:opacity-30"
            >
              {generatingImage === 'nostalgisk' ? <Loader2 size={14} className="animate-spin" /> : <Palette size={14} />}
              {generatingImage === 'nostalgisk'
                ? t('admin.articles_image_generating', 'Genererer...')
                : t('admin.articles_image_style_nostalgic', 'Nostalgisk stil')}
            </button>
          </div>
          {!!generatingImage && (
            <p className="text-xs text-[var(--text-muted)] text-center animate-pulse">
              {t('admin.articles_image_wait', 'AI leser artikkelen og lager illustrasjon... dette kan ta 15-30 sekunder')}
            </p>
          )}

          {/* AI-skjermbilder av selve appen (desktop + mobil) */}
          <button
            onClick={handleCaptureScreenshots}
            disabled={capturingShots}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-card bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/15 transition-all text-2xs font-black uppercase tracking-widest disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
          >
            {capturingShots ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
            {capturingShots
              ? t('admin.articles.capturing_shots_btn', 'Tar skjermbilder …')
              : t('admin.articles.capture_shots_btn', 'AI: skjermbilder av appen (web + mobil)')}
          </button>
        </div>

        {/* YouTube videos — editable */}
        <div className="space-y-2">
          <label className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest px-1">
            YouTube-videoer ({youtubeVideos.length})
          </label>
          {youtubeVideos.length > 0 && (
            <div className="space-y-2">
              {youtubeVideos.map((vid) => (
                <div key={vid.videoId} className="bg-[var(--bg-surface-alt)] border border-red-500/15 rounded-card overflow-hidden">
                  <div className="flex items-center gap-3 p-3">
                    {vid.thumbnailUrl && (
                      <img src={vid.thumbnailUrl} alt="" className="w-24 h-14 object-cover rounded-card flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <p className="text-tiny font-bold text-[var(--text-primary)] leading-snug truncate">{vid.title}</p>
                      <p className="text-xs text-[var(--text-muted)]">{vid.channelTitle}</p>
                      <a href={vid.url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-red-400 hover:text-red-300 transition-colors">
                        <ExternalLink size={10} /> Åpne på YouTube
                      </a>
                    </div>
                    <button onClick={() => handleRemoveYouTubeVideo(vid.videoId)}
                      className="p-1.5 rounded-card hover:bg-red-500/20 text-[var(--text-muted)] hover:text-red-400 transition-colors flex-shrink-0">
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Add YouTube video */}
          <div className="flex gap-2">
            <input
              type="text"
              value={ytUrlInput}
              onChange={e => setYtUrlInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddYouTubeVideo()}
              placeholder="Lim inn YouTube-URL..."
              className="flex-1 bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-card px-3 py-2 text-tiny text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-red-500/30"
            />
            <button
              onClick={handleAddYouTubeVideo}
              disabled={ytAdding || !ytUrlInput.trim()}
              className="px-3 py-2 rounded-card bg-red-500/20 border border-red-500/20 text-red-400 hover:bg-red-500/30 transition-all text-xs font-bold disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
            >
              {ytAdding ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            </button>
          </div>
        </div>

        {/* Artikkeltype: blogg vs versjonsnotat */}
        <div className="p-4 rounded-card border border-[var(--border-primary)] bg-[var(--bg-surface-alt)] space-y-3">
          <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest flex items-center gap-2">
            <Newspaper size={13} /> {t('admin.articles.type_label', 'Artikkeltype')}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setArticleType('blog')}
              className={`flex-1 py-2 rounded-card text-3xs font-black uppercase tracking-widest transition-all ${articleType === 'blog' ? 'bg-emerald-600 text-white' : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
            >
              {t('admin.articles.type_blog', 'Blogg')}
            </button>
            <button
              type="button"
              onClick={() => setArticleType('release_notes')}
              className={`flex-1 py-2 rounded-card text-3xs font-black uppercase tracking-widest transition-all ${articleType === 'release_notes' ? 'bg-emerald-600 text-white' : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
            >
              {t('admin.articles.type_release', 'Versjonsnotat')}
            </button>
          </div>
          {articleType === 'release_notes' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-3xs text-[var(--text-muted)] font-bold uppercase tracking-wider">{t('admin.articles.version_label', 'Versjon')}</label>
                <input
                  value={articleVersion}
                  onChange={(e) => setArticleVersion(e.target.value)}
                  placeholder="2026.07.10"
                  className="mt-1 w-full bg-[var(--bg-surface)] border border-[var(--border-primary)] rounded-card px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-emerald-500/50"
                />
              </div>
              <div>
                <label className="text-3xs text-[var(--text-muted)] font-bold uppercase tracking-wider">{t('admin.articles.impact_label', 'Påvirkning')}</label>
                <div className="mt-1 flex gap-1">
                  <button
                    type="button"
                    onClick={() => setArticleImpact('minor')}
                    className={`flex-1 py-2 rounded-card text-3xs font-black uppercase tracking-widest transition-all ${articleImpact === 'minor' ? 'bg-[var(--text-muted)]/30 text-[var(--text-primary)]' : 'bg-[var(--bg-surface)] text-[var(--text-secondary)]'}`}
                  >
                    {t('admin.articles.impact_minor', 'Liten')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setArticleImpact('major')}
                    className={`flex-1 py-2 rounded-card text-3xs font-black uppercase tracking-widest transition-all ${articleImpact === 'major' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-[var(--bg-surface)] text-[var(--text-secondary)]'}`}
                  >
                    {t('admin.articles.impact_major', 'Stor')}
                  </button>
                </div>
              </div>
            </div>
          )}
          {articleType === 'release_notes' && (
            <p className="text-xs text-[var(--text-muted)] leading-relaxed">
              {t('admin.articles.release_hint', 'Versjonsnotater vises i «Hva er nytt» i appen og som teaser på nettsiden. «Stor» påvirkning anbefaler e-post til alle brukere.')}
            </p>
          )}
        </div>

        {/* Email toggle */}
        {editing.status !== 'published' && (
          <div
            onClick={() => setDistributeEmail(!distributeEmail)}
            className={`flex items-center gap-3 p-4 rounded-card cursor-pointer transition-all border ${distributeEmail ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)] hover:border-[var(--border-secondary)]'}`}
          >
            <div className={`w-10 h-5 rounded-full flex items-center transition-all ${distributeEmail ? 'bg-emerald-500 justify-end' : 'bg-[var(--bg-surface)] justify-start'}`}>
              <div className="w-4 h-4 bg-white rounded-full mx-0.5 shadow-sm" />
            </div>
            <div>
              <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('admin.articles_distribute_email', 'Send som kampanje-epost')}</p>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">
                {distributeEmail ? 'Artikkelen sendes til alle brukere ved publisering' : 'Artikkelen publiseres kun på nettsiden (RSS)'}
              </p>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2 pt-2">
          {editing.status === 'published' ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-card bg-emerald-600/80 text-white hover:bg-emerald-600 transition-all text-2xs font-black uppercase tracking-widest disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {t('admin.articles_save_changes', 'Lagre endringer')}
              </button>
              {confirmCampaign ? (
                <button
                  onClick={handleSendCampaign}
                  disabled={sendingCampaign}
                  className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-card bg-amber-600/80 text-white hover:bg-amber-600 transition-all text-2xs font-black uppercase tracking-widest disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
                >
                  {sendingCampaign ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                  Bekreft sending
                </button>
              ) : (
                <button
                  onClick={() => setConfirmCampaign(true)}
                  className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-card bg-[var(--bg-surface)]/50 border border-[var(--border-secondary)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]/70 transition-all text-2xs font-black uppercase tracking-widest"
                >
                  <Mail size={14} />
                  Send som epost
                </button>
              )}
            </>
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-card bg-[var(--bg-surface)]/50 border border-[var(--border-secondary)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]/70 transition-all text-2xs font-black uppercase tracking-widest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {t('admin.articles_save_draft', 'Lagre utkast')}
              </button>
              <button
                onClick={handlePublish}
                disabled={publishing || !title.trim() || !content.trim()}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-card bg-emerald-600/80 text-white hover:bg-emerald-600 transition-all text-2xs font-black uppercase tracking-widest disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
              >
                {publishing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {t('admin.articles_publish', 'Publiser')}
              </button>
            </>
          )}

          {/* Delete */}
          {confirmDelete ? (
            <div className="flex gap-2 flex-1">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-card bg-red-600/80 text-white text-2xs font-black uppercase tracking-widest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                {t('admin.articles_delete_confirm', 'Bekreft slett')}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-4 py-3.5 rounded-card bg-[var(--bg-surface)]/30 text-[var(--text-secondary)] text-2xs font-black uppercase tracking-widest"
              >
                Avbryt
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className="px-4 py-3.5 rounded-card bg-red-500/10 border border-red-500/20 text-red-400/70 hover:text-red-400 transition-all text-2xs font-black uppercase tracking-widest"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── List View ───
  // Ingen egen høyde-/scroll-boks her. AdminPanel er allerede en modal på 92vh
  // med egen scroll og egen 260px sidemeny; en `max-h-[85vh] overflow-y-auto`
  // inni den ga boks-i-boks og klemte radene sammen. Det var hovedgrunnen til at
  // modulen ikke lignet designretningen fra shotgunen.
  return (
    <div className="space-y-5 animate-in slide-in-from-bottom-4">
      {/* Tre linser på samme datamodell. Innboks er default fordi det du gjør
          oftest er å avgjøre, ikke å planlegge. Flyt kommer med crawleren.
          Se docs/plan-gjenstaende-gap.md Spor F. */}
      <Tabs
        items={[
          { key: 'flyt', label: 'Flyt' },
          { key: 'innboks', label: 'Innboks', count: urgentCount || undefined },
          { key: 'arshjul', label: 'Årshjul' },
          { key: 'alle', label: 'Alle', count: articles.length || undefined },
        ]}
        value={moduleTab}
        onChange={setModuleTab}
        scrollable
        aria-label="Artikkelvisning"
      />

      {/* Hårlinje UNDER fanene, ikke chevroner over. Chevroner antyder at du
          skyver saker mot høyre; statusovergangene er hendelsesdrevet. */}
      <div className="grid grid-cols-2 sm:flex sm:items-stretch border-y border-[var(--border-primary)] divide-x divide-y sm:divide-y-0 divide-[var(--border-primary)]">
        {pipelineCounts.map((c) => (
          <div key={c.key} className="flex-1 px-3 sm:px-4 py-2.5 min-w-0">
            <p className={`text-tiny font-black uppercase tracking-widest truncate ${c.active ? 'text-emerald-400' : 'text-[var(--text-muted)]'}`}>
              {c.label}
            </p>
            <p className={`text-xl font-bold tabular-nums leading-tight ${c.active ? 'text-emerald-400' : 'text-[var(--text-primary)]'}`}>
              {c.n}
            </p>
          </div>
        ))}
      </div>

      {moduleTab === 'flyt' && (
        <div className="bg-[var(--bg-surface)] border border-[var(--border-primary)] rounded-panel p-3 md:p-5">
          <ArticleFlowBoard
            articles={articles}
            busy={publishing || saving}
            onOpen={openEditor}
            onPublish={handleInboxPublish}
            onSchedule={handleInboxSchedule}
          />
        </div>
      )}

      {moduleTab === 'innboks' && (
        <div className="bg-[var(--bg-surface)] border border-[var(--border-primary)] rounded-panel">
          <div className="px-5 pt-5 pb-3">
            <h3 className="text-xl font-bold text-[var(--text-primary)]">Krever din avgjørelse</h3>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              {inboxUrgent.length === 0
                ? 'Ingenting haster. Nye utkast dukker opp her når årshjulet produserer.'
                : 'Publiser sender artikkelen live, med oversettelser og eventuell e-post.'}
            </p>
          </div>
          {inboxUrgent.length > 0 && (
            <div>
              {inboxUrgent.map((a) => (
                <ArticleInboxRow
                  key={a.id}
                  article={a}
                  busy={publishing || saving}
                  onOpen={openEditor}
                  onPublish={handleInboxPublish}
                  onLater={handleInboxLater}
                  onDelete={handleInboxDelete}
                />
              ))}
            </div>
          )}

          {inboxDrafts.length > 0 && (
            <>
              <div className="px-5 pt-4 pb-2 border-t border-[var(--border-primary)]">
                <h3 className="text-sm font-bold text-[var(--text-primary)]">
                  Utkast uten avgjørelse
                  <span className="ml-2 text-xs font-normal text-[var(--text-muted)] tabular-nums">
                    {inboxDrafts.length}
                  </span>
                </h3>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">
                  Ligger her til de publiseres eller slettes. Haster ikke, men blir
                  ikke borte av seg selv.
                </p>
              </div>
              <div>
                {inboxDrafts.map((a) => (
                  <ArticleInboxRow
                    key={a.id}
                    article={a}
                    busy={publishing || saving}
                    onOpen={openEditor}
                    onPublish={handleInboxPublish}
                    onLater={handleInboxLater}
                    onDelete={handleInboxDelete}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {moduleTab === 'arshjul' && (
        /* To kolonner: stripa ble designet som en smal sidekolonne (variant F).
           I full bredde fløt hver merkedag alene med hundrevis av piksler tomrom
           til høyre, og det var hovedgrunnen til at visningen så uferdig ut. */
        <div className="grid lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] gap-4 items-start">
          <ArticleYearStrip
            entries={wheelEntries}
            today={todayYmd()}
            planHorizonYmd={wheelHorizon}
            onSelect={setSelectedWheelId}
            selectedId={selectedWheelId}
            articles={articles}
            onOpenArticle={openEditor}
          />
          <AdminArticleWheelPanel selectedId={selectedWheelId} />
        </div>
      )}

      <div
        className="bg-[var(--bg-surface)] border border-[var(--border-primary)] p-4 md:p-6 rounded-panel space-y-4"
        hidden={moduleTab !== 'alle'}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-emerald-400">
            <Newspaper size={20} />
            <div>
              <h3 className="text-sm font-bold text-[var(--text-primary)]">{t('admin.articles_title', 'Artikler')}</h3>
              <p className="text-xs font-bold opacity-70">{t('admin.articles_subtitle', 'AI-genererte artikler og blogginnlegg')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowRssInfo(!showRssInfo)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-card border transition-all text-2xs font-black uppercase tracking-widest ${
                showRssInfo
                  ? 'bg-amber-500/20 border-amber-500/40 text-amber-400'
                  : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)] text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--border-secondary)]'
              }`}
              title="RSS Feed"
            >
              <Rss size={14} />
            </button>
            <button
              onClick={() => setShowCustomTopic(!showCustomTopic)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-card border transition-all text-2xs font-black uppercase tracking-widest ${
                showCustomTopic
                  ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
                  : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)] text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--border-secondary)]'
              }`}
              title={t('admin.articles_custom_topic', 'Egendefinert tema')}
            >
              <BrainCircuit size={14} />
            </button>
            <Button
              variant="secondary"
              size="md"
              onClick={handleGenerate}
              disabled={generating || creating}
              loading={generating}
              icon={<BrainCircuit size={14} />}
            >
              {generating ? t('admin.articles_generating', 'Genererer...') : t('admin.articles_generate_ai', 'AI-generer')}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleCreate}
              disabled={creating || generating}
              loading={creating}
              icon={<Plus size={14} />}
            >
              {t('admin.articles_new', 'Ny artikkel')}
            </Button>
          </div>
        </div>

        {showRssInfo && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-card p-4 space-y-2 animate-in slide-in-from-top-2">
            <div className="flex items-center gap-2 text-amber-400">
              <Rss size={14} />
              <span className="text-2xs font-black uppercase tracking-widest">RSS / XML Feed</span>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card px-3 py-2 text-xs text-[var(--text-secondary)] font-mono truncate">
                {RSS_FEED_URL}
              </code>
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(RSS_FEED_URL);
                  } catch {
                    const ta = document.createElement('textarea');
                    ta.value = RSS_FEED_URL;
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                  }
                  setRssCopied(true);
                  setTimeout(() => setRssCopied(false), 3000);
                }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-card text-3xs font-black uppercase tracking-widest transition-all ${
                  rssCopied
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-amber-500/10 border border-amber-500/20 text-amber-400 hover:bg-amber-500/20'
                }`}
              >
                {rssCopied ? <Check size={12} /> : <Copy size={12} />}
                {rssCopied ? t('common.copied', 'Kopiert!') : t('common.copy', 'Kopier')}
              </button>
            </div>
            <p className="text-xs text-[var(--text-muted)]">
              {t('admin.articles_rss_help', 'Bruk denne URL-en i RSS-lesere, sosiale medier eller andre tjenester som støtter RSS/Atom-feeds.')}
            </p>
          </div>
        )}

        {showCustomTopic && (
          <div className="bg-[var(--bg-surface)] border border-[var(--border-primary)] rounded-card p-4 space-y-2 animate-in slide-in-from-top-2">
            <div className="flex items-center gap-2 text-emerald-400">
              <BrainCircuit size={14} />
              <span className="text-2xs font-black uppercase tracking-widest">
                {t('admin.articles_custom_topic', 'Egendefinert tema')}
              </span>
            </div>
            <textarea
              value={customTopic}
              onChange={(e) => setCustomTopic(e.target.value)}
              placeholder={t('admin.articles_custom_topic_placeholder', 'Valgfritt: beskriv tema, f.eks. "Solcellepanel for bobilister" eller "Tips for Lofoten-tur"')}
              rows={2}
              className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card p-3 text-tiny text-[var(--text-secondary)] focus:outline-none focus:border-emerald-500/50 resize-none leading-relaxed placeholder:text-[var(--text-muted)]"
            />
            <textarea
              value={articleAngle}
              onChange={(e) => setArticleAngle(e.target.value)}
              placeholder={t('admin.articles_angle_placeholder', 'Valgfritt: gi artikkelen en vinkling, f.eks. "skriv for nybegynnere", "fokuser på budsjett" eller "løft frem familietur"')}
              rows={3}
              className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card p-3 text-tiny text-[var(--text-secondary)] focus:outline-none focus:border-emerald-500/50 resize-none leading-relaxed placeholder:text-[var(--text-muted)]"
            />
            <p className="text-xs text-[var(--text-muted)]">
              {t('admin.articles_custom_topic_help', 'Tomme felt = standard sesongbasert generering. Tema styrer hva artikkelen handler om, vinkling styrer hvordan den skal angripes.')}
            </p>
          </div>
        )}

        {actionMsg && (
          <div className={`text-xs font-bold px-4 py-2 rounded-card ${actionMsg.startsWith('Feil') || actionMsg.startsWith('Ugyldig') || actionMsg.startsWith('Kunne ikke') || actionMsg.startsWith('Videoen er allerede') ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
            {actionMsg}
          </div>
        )}

        {/* Triagering: stegene i løypa som filtre, med tellere. Uten dette er
            «Alle» en flat liste der status bare er et merke på hver rad. */}
        {articles.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {STATUS_FILTERS.map((f) => {
              const n = articles.filter(f.match).length;
              if (n === 0 && f.key !== 'alle') return null;
              const active = statusFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setStatusFilter(f.key)}
                  aria-pressed={active}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-pill border text-tiny font-black uppercase tracking-widest transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 ${
                    active
                      ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
                      : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)] text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                  }`}
                >
                  {f.label}
                  <span className="tabular-nums opacity-70">{n}</span>
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-12">
            <FileText size={32} className="mx-auto text-[var(--text-muted)] mb-3" />
            <p className="text-tiny text-[var(--text-muted)] font-bold">{t('admin.articles_no_articles', 'Ingen artikler ennå')}</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">AI-generatoren lager utkast den 1. og 15. hver måned</p>
          </div>
        ) : (
          <div className="space-y-2">
            {visibleArticles.map((article) => (
              article.status === 'generating' ? (
                /* Generating placeholder — animated card */
                <div
                  key={article.id}
                  className="w-full flex items-center gap-3 p-3 rounded-card bg-[var(--bg-surface)] border border-[var(--border-primary)] animate-pulse"
                >
                  <div className="w-12 h-12 rounded-card bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <BrainCircuit size={16} className="text-emerald-400 animate-spin" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-tiny font-bold text-emerald-300 truncate">
                      {article.title || t('admin.articles_generating_title', 'AI genererer artikkel...')}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-4xs font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">
                        {t('admin.articles_status_generating', 'Genererer...')}
                      </span>
                      <span className="text-xs text-[var(--text-muted)]">
                        {t('admin.articles_generation_wait', 'YouTube + AI-tekst + illustrasjon — kan ta 1-2 min')}
                      </span>
                    </div>
                  </div>
                  <Loader2 size={16} className="text-emerald-400 animate-spin flex-shrink-0" />
                </div>
              ) : (
                /* Rad = div med role="button", ikke <button>. Raden inneholder egne
                   knapper (forhåndsvisning), og <button> i <button> er ugyldig HTML:
                   det ga hydreringsfeil og ødela tastaturnavigasjonen. */
                <div
                  key={article.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openEditor(article)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEditor(article); }
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-card bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] hover:border-emerald-500/20 hover:bg-[var(--bg-surface-alt)] transition-all text-left group cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
                >
                  {/* Thumbnail */}
                  {article.images?.length > 0 ? (
                    <img
                      src={article.images.sort((a, b) => a.order - b.order)[0].url}
                      alt=""
                      className="w-12 h-12 object-cover rounded-card flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-card bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                      <FileText size={16} className="text-emerald-500/50" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-tiny font-bold text-[var(--text-primary)] truncate group-hover:text-emerald-300 transition-colors">
                      {article.title || 'Uten tittel'}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-4xs font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_BADGE[article.status] ?? STATUS_BADGE.draft}`}>
                        {article.status === 'published' ? t('admin.articles_status_published', 'Publisert')
                          : article.status === 'pending_review' ? t('admin.articles_status_pending_review', 'Til godkjenning')
                          : article.status === 'approved' ? t('admin.articles_status_approved', 'Godkjent')
                          : t('admin.articles_status_draft', 'Utkast')}
                      </span>
                      {article.status === 'approved' && article.scheduled_publish_ymd && (
                        <span className="text-tiny font-bold text-emerald-400/70">
                          {article.scheduled_publish_ymd}
                        </span>
                      )}
                      {article.generated_by === 'ai' && (
                        <span className="text-tiny font-bold text-emerald-400/60">AI</span>
                      )}
                      {(article.youtube_videos?.length || article.youtube_video) && (
                        <Youtube size={10} className="text-red-400/60" />
                      )}
                      <span className="text-xs text-[var(--text-muted)]">
                        {formatDate(article.status === 'published' ? article.published_at : article.created_at)}
                      </span>
                      {/* Visninger vises kun for publiserte — et utkast har ingen
                          lesere, og «0» der ville lest som en dårlig artikkel. */}
                      {article.status === 'published' && (
                        <span
                          className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] tabular-nums"
                          title="Lesninger (bot-filtrert)"
                        >
                          <Eye size={10} />
                          {(article.view_count ?? 0).toLocaleString('nb-NO')}
                          <ViewSparkline viewsByDay={article.views_by_day} className="text-emerald-400/70" />
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Preview button */}
                  {article.content && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setPreviewArticle(article); }}
                      className="p-2 rounded-card hover:bg-emerald-500/10 text-[var(--text-muted)] hover:text-emerald-400 transition-colors flex-shrink-0 sm:opacity-0 sm:group-hover:opacity-100 min-w-[44px] min-h-[44px] flex items-center justify-center"
                      title={t('admin.articles_preview', 'Forhåndsvisning')}
                    >
                      <Eye size={14} />
                    </button>
                  )}
                </div>
              )
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewArticle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setPreviewArticle(null)}>
          <div
            className="bg-white rounded-card w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Hero image */}
            {previewArticle.images?.length > 0 && (
              <img
                src={previewArticle.images.sort((a, b) => a.order - b.order)[0].url}
                alt=""
                className="w-full h-48 object-cover rounded-t-2xl"
              />
            )}

            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-xl font-bold text-[var(--text-primary)] leading-tight">{previewArticle.title || 'Uten tittel'}</h1>
                  {previewArticle.summary && (
                    <p className="text-sm text-[var(--text-muted)] mt-2 leading-relaxed italic">{previewArticle.summary}</p>
                  )}
                </div>
                <button
                  onClick={() => setPreviewArticle(null)}
                  className="p-2 rounded-full hover:bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] flex-shrink-0"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Article content */}
              <div
                className="prose prose-sm max-w-none text-[var(--text-secondary)] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: previewArticle.content }}
              />

              {/* YouTube videos in preview */}
              {(() => {
                const vids = previewArticle.youtube_videos?.length
                  ? previewArticle.youtube_videos
                  : previewArticle.youtube_video
                    ? [previewArticle.youtube_video]
                    : [];
                if (vids.length === 0) return null;
                return (
                  <div className="border-t border-[var(--border-primary)] pt-4 space-y-2">
                    <p className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider">Anbefalte videoer</p>
                    {vids.map(v => (
                      <a key={v.videoId} href={v.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 p-2 rounded-card hover:bg-[var(--bg-surface-alt)] transition-colors">
                        {v.thumbnailUrl && <img src={v.thumbnailUrl} alt="" className="w-20 h-12 object-cover rounded" />}
                        <div>
                          <p className="text-[12px] font-semibold text-[var(--text-primary)]">{v.title}</p>
                          <p className="text-xs text-[var(--text-muted)]">{v.channelTitle}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                );
              })()}

              {/* Linked content in preview */}
              {previewArticle.linked_content && previewArticle.linked_content.length > 0 && (
                <div className="border-t border-[var(--border-primary)] pt-4 space-y-2">
                  <p className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1.5">
                    <Link2 size={12} />
                    {t('admin.articles_linked_content_preview', 'Se også')}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {previewArticle.linked_content.map(item => (
                      <a
                        key={`${item.type}-${item.id}`}
                        href={getLinkUrl(item)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 rounded-card border border-[var(--border-primary)] hover:border-emerald-400 hover:bg-teal-50/50 transition-all group"
                      >
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt="" className="w-12 h-12 object-cover rounded-card flex-shrink-0" />
                        ) : (
                          <div className="w-12 h-12 rounded-card bg-[var(--bg-surface-alt)] flex items-center justify-center flex-shrink-0">
                            {item.type === 'route' ? <Route size={16} className="text-emerald-500" /> : <MapPin size={16} className="text-emerald-500" />}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] font-semibold text-[var(--text-primary)] group-hover:text-emerald-700 truncate">{item.name}</p>
                          <p className="text-xs text-[var(--text-secondary)]">
                            {item.type === 'route' ? t('admin.articles_link_route', 'Rute') : t('admin.articles_link_spot', 'Plass')}
                          </p>
                        </div>
                        <ExternalLink size={12} className="text-[var(--text-secondary)] group-hover:text-emerald-500 flex-shrink-0" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {previewArticle.external_links && previewArticle.external_links.length > 0 && (
                <div className="border-t border-[var(--border-primary)] pt-4 space-y-2">
                  <p className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1.5">
                    <ExternalLink size={12} />
                    {t('admin.articles_external_links', 'Eksterne lenker')}
                  </p>
                  <div className="space-y-2">
                    {previewArticle.external_links.map((item, idx) => (
                      <a
                        key={`${item.url}-${idx}`}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 rounded-card border border-[var(--border-primary)] hover:border-emerald-400 hover:bg-blue-50/50 transition-all group"
                      >
                        <p className="text-[12px] font-semibold text-[var(--text-primary)] group-hover:text-emerald-700 truncate">{item.title}</p>
                        <p className="text-xs text-[var(--text-secondary)] truncate">{item.url}</p>
                        {item.comment && <p className="text-tiny text-[var(--text-muted)] mt-1">{item.comment}</p>}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminArticlesPanel;
